// src/components/AIChat.js
import React, { useState, useEffect, useRef } from "react";
import { Bot, Paperclip, Send, X, Image, Search } from "lucide-react";
import ModelSelector from "./ModelSelector";
import ChatContainer from "./ChatContainer";
import Layout from "./Layout";
import SessionTracker from "./SessionTracker";
import TokenDisplay from "./TokenDisplay";
import { useFileUpload } from "../hooks/useFileUpload";
import { useTokenCalculator } from "../hooks/useTokenCalculator";
import {
  handleContextLengthError,
  saveModelLimit,
} from "../utils/modelLimits";
import { calculateMessageCost } from "../utils/tokenUtils";
import { checkAndResetSession } from "../utils/sessionStorage";
import { useSessionPersistence } from "../hooks/useSessionPersistence";
import { logUsageState } from "../utils/debugUsage";
import ChatSidebar from "./ChatSidebar";
import { createMessagesArray } from "../utils/messageUtils";
import { useModels } from "../hooks/useModels";
import "./AIChat.css";

const AIChat = ({ showHistory, onCloseHistory }) => {
  const fileInputRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(false);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Handle desktop sidebar toggle
  useEffect(() => {
    if (!isMobile && showHistory !== undefined) {
      setDesktopSidebarOpen(showHistory);
    }
  }, [showHistory, isMobile]);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const { defaultModel } = useModels('chat');
  const [selectedModel, setSelectedModel] = useState("");
  const [userStats, setUserStats] = useState({
    balance: 0,
    totalTokensUsed: 0,
    totalCost: 0
  });
  
  // Load user stats on component mount
  useEffect(() => {
    const loadUserStats = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/api/user/profile`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUserStats({
            balance: userData.balance || 0,
            totalTokensUsed: userData.totalTokensUsed || 0,
            totalCost: userData.totalCost || 0
          });
        }
      } catch (error) {
        console.error('Failed to load user stats:', error);
      }
    };
    
    loadUserStats();
  }, []);
  const [isLoading, setIsLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const messagesEndRef = useRef(null);
  const userId = "ai_chat_user";

  const sessionConfig = {
    maxTokensPerSession: 10000,
    tokensPerHour: 500,
    membershipLevel: "basic",
  };

  const [modelsData, setModelsData] = useState({ models: [] });
  
  // Load models data for file upload capabilities
  useEffect(() => {
    const loadModelsData = async () => {
      try {
        const chatResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/api/models/chat`, {
          credentials: 'include'
        });
        if (chatResponse.ok) {
          const data = await chatResponse.json();
          setModelsData(data);
          window.modelsCache = data;
        }
      } catch (error) {
        console.error('Failed to load models data:', error);
      }
    };
    loadModelsData();
  }, []);

  const {
    attachments,
    setAttachments,
    handleDrop,
    handleDragOver,
    handlePaste,
    handleFileUpload,
    removeAttachment,
    supportsVision,
  } = useFileUpload(selectedModel, modelsData);

  const tokenStats = useTokenCalculator(
    messages,
    inputMessage,
    selectedModel,
    attachments,
    '', // allProjectFiles
    false, // includeCurrentFiles
    [] // selectedFiles
  );

  useSessionPersistence("ai_chat");

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    // Only scroll to bottom when a new complete message is added
    // Don't scroll during streaming updates
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && !lastMessage.isStreaming) {
      scrollToBottom();
    }
  }, [messages]);

  useEffect(() => {
    if (defaultModel && !selectedModel) {
      setSelectedModel(defaultModel);
    }
  }, [defaultModel, selectedModel]);

  useEffect(() => {
    checkAndResetSession("ai_chat");
    loadChatHistory();
  }, []);

  const loadChatHistory = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/api/chat-history?mode=chat`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setChatHistory(data.chats || []);
      }
    } catch (error) {
      console.error("Failed to load chat history:", error);
    }
  };

  const loadChat = async (chatId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/api/chat-history/${chatId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.chat) {
          setCurrentChatId(chatId);
          setMessages(data.chat.messages || []);
          onCloseHistory();
        }
      }
    } catch (error) {
      console.error("Failed to load chat:", error);
    }
  };

  const saveChatHistory = async (
    chatId,
    messages,
    title,
    totalTokens = 0,
    totalCost = 0
  ) => {
    try {
      const chatData = {
        id: chatId,
        title: title || "New Chat",
        messages: messages,
        totalTokens: totalTokens,
        totalCost: totalCost,
        mode: 'chat'
      };
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/api/chat-history`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(chatData)
      });
      
      if (response.ok) {
        loadChatHistory();
      }
    } catch (error) {
      console.error("Failed to save chat history:", error);
    }
  };

  const createNewChat = () => {
    const newChatId =
      "chat_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
    setCurrentChatId(newChatId);
    setMessages([]);
    onCloseHistory();
  };

  const getChatTitle = (messages) => {
    const userMessages = messages.filter(
      (msg) => msg.role === "user" && !msg.isSystemMessage
    );
    if (userMessages.length > 0) {
      return (
        userMessages[0].content.slice(0, 30) +
        (userMessages[0].content.length > 30 ? "..." : "")
      );
    }
    return "New Chat";
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !selectedModel) return;

    const userMessage = {
      role: "user",
      content: inputMessage,
      attachments: [...attachments],
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => {
      const newMessages = [...prev, userMessage];
      if (!currentChatId) {
        const newChatId =
          "chat_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
        setCurrentChatId(newChatId);
      }
      return newMessages;
    });

    logUsageState("AIChat", window.sessionStats);
    setInputMessage("");
    setAttachments([]);
    setIsLoading(true);

    try {
      const requestBody = {
        model: selectedModel,
        message: inputMessage,
        conversation: messages.filter(msg => !msg.isSystemMessage).map(msg => ({
          role: msg.role,
          content: msg.content
        })),
        attachments: attachments.map(att => {
          // Ensure we have the data for file attachments
          let attachmentData = att.data;
          if (!attachmentData) {
            console.warn('Attachment missing data, skipping:', att.name);
            return null;
          }
          
          console.log('Sending attachment:', {
            type: att.type,
            name: att.name,
            dataLength: attachmentData?.length || 0,
            size: att.size,
            mimeType: att.mimeType,
            extractText: att.extractText
          });
          
          return {
            type: att.type,
            name: att.name,
            data: attachmentData,
            size: att.size,
            mimeType: att.mimeType,
            extractText: att.extractText || false,
            isPdfFile: att.isPdfFile || false,
            isDocFile: att.isDocFile || false
          };
        }).filter(att => att !== null)
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/api/chat/completions`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        let errorMessage =
          errorData.error?.message ||
          `HTTP ${response.status}: ${response.statusText}`;

        if (
          errorMessage.includes("context length") ||
          errorMessage.includes("token limit")
        ) {
          const wasHandled = handleContextLengthError(
            errorMessage,
            selectedModel,
            messages,
            setMessages,
            saveModelLimit
          );
          if (wasHandled) {
            setIsLoading(false);
            return;
          }
        }
        throw new Error(errorMessage);
      }

      // Handle streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let aiResponse = "";
      let updateQueue = [];
      let isUpdating = false;
      let hasScrolledToMessage = false;
      let imageGenerated = null;
      
      // Add initial assistant message and stop loading immediately
      const assistantMessageId = Date.now();
      setMessages((prev) => [
        ...prev,
        {
          id: assistantMessageId,
          role: "assistant",
          content: "",
          timestamp: new Date().toISOString(),
          isStreaming: true,
          image: null
        },
      ]);
      
      // Stop the loading animation immediately when streaming starts
      setIsLoading(false);

      // Optimized update function for mobile performance
      const updateMessage = () => {
        if (isUpdating) return;
        isUpdating = true;
        
        // Use setTimeout for better mobile compatibility
        setTimeout(() => {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantMessageId
                ? { ...msg, content: aiResponse, image: imageGenerated }
                : msg
            )
          );
          isUpdating = false;
        }, 0);
      };

      // Batch updates with adaptive timing for mobile
      let batchTimeout;
      const batchedUpdate = () => {
        clearTimeout(batchTimeout);
        // Use longer debounce on mobile for better performance
        const debounceTime = isMobile ? 100 : 50;
        batchTimeout = setTimeout(updateMessage, debounceTime);
      };

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data === '[DONE]') {
                continue;
              }
              
              try {
                const parsed = JSON.parse(data);
                
                if (parsed.type === 'content' && parsed.content) {
                  aiResponse += parsed.content;
                  console.log(`[Client] 📥 Received token: "${parsed.content.replace(/\n/g, '\\n')}" - Total length: ${aiResponse.length}`);
                  
                  // Only scroll once when streaming starts (first token received)
                  if (!hasScrolledToMessage && aiResponse.length > 0) {
                    hasScrolledToMessage = true;
                    setTimeout(() => {
                      scrollToBottom();
                    }, 100);
                  }
                  
                  // Use much less frequent updates to reduce jarring experience
                  batchedUpdate();
                } else if (parsed.type === 'image' && parsed.imageUrl) {
                  // Handle image generation
                  imageGenerated = parsed.imageUrl;
                  batchedUpdate();
                } else if (parsed.type === 'search' && parsed.results) {
                  // Handle search results
                  aiResponse += `\n\n**Search Results:**\n${parsed.results.map((r, i) => `${i+1}. [${r.title}](${r.url})`).join('\n')}`;
                  batchedUpdate();
                } else if (parsed.type === 'done') {
                  // Clear any pending batch updates
                  clearTimeout(batchTimeout);
                  
                  // Final update with complete content
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === assistantMessageId
                        ? { ...msg, content: aiResponse, isStreaming: false, image: imageGenerated }
                        : msg
                    )
                  );
                  
                  // Update user stats from server response
                  if (parsed.userStats) {
                    setUserStats({
                      balance: parsed.userStats.balance,
                      totalTokensUsed: parsed.userStats.totalTokensUsed,
                      totalCost: parsed.userStats.totalCost
                    });
                    
                    // Emit event for other components
                    window.dispatchEvent(new CustomEvent('userStatsUpdated', {
                      detail: {
                        balance: parsed.userStats.balance,
                        totalTokensUsed: parsed.userStats.totalTokensUsed,
                        totalCost: parsed.userStats.totalCost
                      }
                    }));
                  }
                  
                  // Update session tokens
                  if (parsed.usage) {
                    const { total_tokens } = parsed.usage;
                    if (window.consumeTokens) {
                      window.consumeTokens(total_tokens);
                    }
                  }
                  
                  // Save chat history
                  const chatId =
                    currentChatId ||
                    "chat_" +
                      Date.now() +
                      "_" +
                      Math.random().toString(36).substr(2, 9);
                  
                  setMessages((finalMessages) => {
                    const finalMsg = finalMessages.find(m => m.id === assistantMessageId);
                    if (finalMsg) {
                      const actualCost = calculateMessageCost(
                        inputMessage,
                        finalMsg.content,
                        selectedModel
                      );
                      
                      const existingChat = chatHistory.find((chat) => chat.id === chatId);
                      const newTotalTokens =
                        (existingChat?.totalTokens || 0) + actualCost.totalTokens;
                      const newTotalCost =
                        (existingChat?.totalCost || 0) + actualCost.totalCost;

                      setTimeout(() => {
                        saveChatHistory(
                          chatId,
                          finalMessages,
                          getChatTitle(finalMessages),
                          newTotalTokens,
                          newTotalCost
                        );
                      }, 100);
                    }
                    return finalMessages;
                  });
                } else if (parsed.type === 'error') {
                  throw new Error(parsed.error || 'Streaming error');
                }
              } catch (parseError) {
                console.warn('Failed to parse streaming data:', parseError);
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      const errorMessage = {
        role: "assistant",
        content: `Error: ${error.message}`,
        timestamp: new Date().toISOString(),
        isError: true,
      };

      setMessages((prev) => [...prev, errorMessage]);
    }

    setIsLoading(false);
  };

  // Check if we should show token display
  const shouldShowTokenDisplay = inputMessage.trim() !== "" || attachments.length > 0;

  return (
    <Layout
      title="AI Chat"
      icon={<Bot size={32} />}
      headerActions={
        <SessionTracker
          maxSessionTokens={sessionConfig.maxTokensPerSession}
          tokensPerHour={sessionConfig.tokensPerHour}
          className="header-session-tracker"
        />
      }
      className="ai-chat-layout"
    >
      <ChatSidebar
        chatHistory={chatHistory}
        currentChatId={currentChatId}
        onLoadChat={loadChat}
        onDeleteChat={async (id) => {
          try {
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/api/chat-history/${id}`, {
              method: 'DELETE',
              credentials: 'include'
            });
            if (response.ok) {
              loadChatHistory();
              if (currentChatId === id) {
                createNewChat();
              }
            }
          } catch (error) {
            console.error('Failed to delete chat:', error);
          }
        }}
        onCreateNewChat={createNewChat}
        isOpen={isMobile ? showHistory : desktopSidebarOpen}
        onToggle={() => {
          console.log('AIChat ChatSidebar onToggle called, isMobile:', isMobile, 'showHistory:', showHistory);
          if (isMobile) {
            if (onCloseHistory) {
              onCloseHistory();
            }
          } else {
            if (onCloseHistory) {
              onCloseHistory();
            }
          }
        }}
      />

      <ChatContainer
        messages={messages}
        isLoading={isLoading}
        onSendMessage={sendMessage}
        inputMessage={inputMessage}
        setInputMessage={setInputMessage}
        attachments={attachments}
        onFileUpload={handleFileUpload}
        onRemoveAttachment={removeAttachment}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onPaste={handlePaste}
        disabled={!selectedModel}
        placeholder="Type your message..."
        selectedModel={selectedModel}
        supportsVision={supportsVision}
        tokenStats={tokenStats}
        sidebarOpen={isMobile ? showHistory : desktopSidebarOpen}
        hideInputArea={true}
      />

      <div className={`bottom-navigation ${isMobile ? (showHistory ? 'sidebar-open' : '') : (desktopSidebarOpen ? 'sidebar-open' : '')}`}>
        {shouldShowTokenDisplay && (
          <div className="token-display-section">
            <TokenDisplay tokenStats={tokenStats} />
          </div>
        )}
        <div className="input-section">
          <div className="input-area">
            {attachments.length > 0 && (
              <div className="attachments-preview">
                {attachments.map((attachment, index) => (
                  <div key={index} className="attachment-item">
                    {attachment.type === 'image' ? (
                      <img 
                        src={attachment.data} 
                        alt={attachment.name}
                        className="attachment-thumb"
                      />
                    ) : (
                      <div className="file-attachment">
                        <Paperclip size={16} />
                        <span>{attachment.name}</span>
                      </div>
                    )}
                    <button 
                      className="remove-attachment"
                      onClick={() => removeAttachment(index)}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <div className="input-row">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                multiple
                accept={(() => {
                  const acceptedTypes = [];
                  
                  const modelsCache = window.modelsCache;
                  if (modelsCache && selectedModel) {
                    const model = modelsCache.models?.find(m => m.id === selectedModel);
                    if (model && model.acceptedFiles) {
                      if (model.acceptedFiles.includes('image')) acceptedTypes.push('image/*');
                      if (model.acceptedFiles.includes('pdf')) acceptedTypes.push('.pdf');
                      if (model.acceptedFiles.includes('docx')) acceptedTypes.push('.doc,.docx');
                      if (model.acceptedFiles.includes('txt')) acceptedTypes.push('.txt');
                      if (model.acceptedFiles.includes('csv')) acceptedTypes.push('.csv');
                    }
                  }
                  
                  if (acceptedTypes.length === 0) {
                    if (supportsVision) acceptedTypes.push('image/*');
                    if (tokenStats?.acceptedFiles?.includes('pdf')) acceptedTypes.push('.pdf');
                    if (tokenStats?.acceptedFiles?.includes('docx')) acceptedTypes.push('.doc,.docx');
                    if (tokenStats?.acceptedFiles?.includes('txt')) acceptedTypes.push('.txt');
                    if (tokenStats?.acceptedFiles?.includes('csv')) acceptedTypes.push('.csv');
                  }
                  
                  return acceptedTypes.length > 0 ? acceptedTypes.join(',') : '.txt';
                })()}
                style={{ display: 'none' }}
              />
              <button 
                className="attachment-btn"
                onClick={() => fileInputRef.current?.click()}
                title={(() => {
                  const modelsCache = window.modelsCache;
                  if (modelsCache && selectedModel) {
                    const model = modelsCache.models?.find(m => m.id === selectedModel);
                    if (model && model.acceptedFiles) {
                      const types = model.acceptedFiles.join(', ');
                      return `Upload files: ${types}`;
                    }
                  }
                  return supportsVision ? "Upload files or images" : "Upload text files only (this model doesn't support images)";
                })()}
              >
                <Paperclip size={20} />
              </button>
              
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Type your message..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                onPaste={handlePaste}
                className="message-input"
                rows="1"
                disabled={!selectedModel}
              />
              
              <button 
                onClick={sendMessage}
                disabled={!inputMessage.trim() || !selectedModel}
                className="send-btn"
              >
                <Send size={20} />
              </button>
            </div>
          </div>
        </div>
        <div className="model-selector-section">
          <ModelSelector
            selectedModel={selectedModel}
            onModelSelect={setSelectedModel}
            hasAttachments={attachments.length > 0}
            mode="chat"
          />
        </div>
      </div>
    </Layout>
  );
};

export default AIChat;